import { useState } from 'react';
import { Loader2Icon, SaveIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { createRegion, updateRegion, type RegionRecord } from '@/lib/auth-client';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel, FieldGroup } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

type RegionFormProps = {
  initialData?: RegionRecord;
  isEdit?: boolean;
  onCancel?: () => void;
  onSuccess?: () => void;
  surface?: 'embedded' | 'standalone';
};

function getInitialFormData(initialData?: RegionRecord) {
  return {
    name: initialData?.name ?? '',
    code: initialData?.code ?? '',
    description: initialData?.description ?? '',
    status: (initialData?.status === 'Inactive' ? 'Inactive' : 'Active') as 'Active' | 'Inactive',
  };
}

export function RegionForm({
  initialData,
  isEdit = false,
  onCancel,
  onSuccess,
  surface = 'standalone',
}: RegionFormProps) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(() => getInitialFormData(initialData));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);

    const promise = isEdit && initialData
      ? updateRegion(initialData.id, formData)
      : createRegion(formData);

    const { error } = await promise;

    if (error) {
      toast.error(isEdit ? "Erreur lors de la modification" : "Erreur lors de la création");
    } else {
      toast.success(isEdit ? "Région enregistrée" : "Région créée");
      onSuccess?.();
      if (!onSuccess) {
        navigate('/admin/regions');
      }
    }
    setIsSubmitting(false);
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'space-y-8 rounded-[var(--radius-surface-4xl)] border border-border/70 bg-card p-6 shadow-[0_8px_24px_rgba(15,23,42,0.03)]',
        surface === 'embedded' ? 'border-0 p-0 shadow-none' : undefined,
      )}
    >
      <FieldGroup>
        <div className="grid gap-6 lg:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="region-name">Nom de la région <span className="text-destructive">*</span></FieldLabel>
            <Input
              id="region-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="ex: Région Île-de-France"
              required
              className="h-12"
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="region-code">Code Région (identifiant URL) <span className="text-destructive">*</span></FieldLabel>
            <Input
              id="region-code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase().replace(/\s+/g, '-') })}
              placeholder="ex: IDF-01"
              required
              className="h-12 font-mono uppercase"
            />
            <p className="mt-2 text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Utilisé pour les URLs fixes des lots.</p>
          </Field>
        </div>

        <Field>
          <FieldLabel htmlFor="region-description">Description (Optionnel)</FieldLabel>
          <Input
            id="region-description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Notes internes..."
            className="h-12"
          />
        </Field>

        {isEdit && (
          <Field>
            <FieldLabel>Statut de la région</FieldLabel>
            <div className="flex gap-3">
              {(['Active', 'Inactive'] as const).map((s) => (
                <Button
                    key={s}
                    type="button"
                    variant={formData.status === s ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, status: s })}
                  className="flex-1 h-12"
                >
                  {s}
                </Button>
              ))}
            </div>
          </Field>
        )}

        <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            className="h-11 px-6"
            onClick={() => {
              onCancel?.();
              if (!onCancel) {
                navigate('/admin/regions');
              }
            }}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="h-11 min-w-32 border-black bg-black text-white shadow-none transition-colors hover:border-black/85 hover:bg-black/85 hover:text-white dark:border-border dark:bg-muted dark:text-foreground dark:hover:border-foreground/20 dark:hover:bg-muted/80"
          >
            {isSubmitting ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : <SaveIcon className="mr-2 size-4" />}
            {isEdit ? 'Mettre à jour la région' : 'Créer la région'}
          </Button>
        </div>
      </FieldGroup>
    </form>
  );
}
